import { getNodeAttributes, getNodeMarkup, getTempNodeMarkup } from './markup'
import { dia, util } from '@clientio/jointjs'
import { isEqual } from 'lodash'
import { NodeType } from '../../../core/workflow'

export const NodeOptCache = '_nodeOptCache'

/**
 * Class representing a diagram node.
 * @extends dia.Element
 */
export class DiagramNode extends dia.Element {
  constructor(props) {
    super(props)
  }

  /**
   * Creates a new DiagramNode instance with the given options.node create opt:
   * @param {CanvasWorkflowNode} opt - The options for the node.
   * @returns {dia.Element} The created DiagramNode instance.
   */
  static create(opt) {
    const node = new DiagramNode(getNodeAttributes(opt))
    node.setCacheOpt(opt)
    if (opt.type === NodeType.TEMPORARY) {
      node.set('markup', getTempNodeMarkup())
    } else {
      node.set('markup', getNodeMarkup(opt.type, opt.icon))
    }
    node.updateNodeLabel(opt.label)
    return node
  }

  /**
   * Updates the node with the given options.
   * If the label, description or position has changed, it updates the node accordingly.
   * @param {CanvasWorkflowNode} opt - The options to update the node with.
   */
  updateNode(opt) {
    const prevOpt = this.getCachedOpt()

    if (prevOpt.label !== opt.label) {
      this.updateNodeLabel(opt.label)
    }

    if (prevOpt.description !== opt.description) {
      this.updateNodeDescription(opt.description)
    }

    if (!isEqual(opt.position, this.position().toJSON())) {
      this.position(opt.position.x, opt.position.y, { deep: true })
    }

    // Update validation icons if validation changed
    if (prevOpt?.validation?.isValid !== opt?.validation?.isValid) {
      const attrs = {
        ...this.attributes.attrs,
        errorIcon: {
          ...this.attributes.attrs.errorIcon,
          style: !opt?.validation?.isValid ? 'display: flex;' : 'display: none;'
        },
        successIcon: {
          ...this.attributes.attrs.successIcon,
          style: opt?.validation?.isValid ? 'display: flex;' : 'display: none;'
        }
      }
      this.set('attrs', attrs, { rewrite: true })
    }

    this.setCacheOpt(opt)
  }

  /**
   * Gets the label of the node.
   * @returns {string} The label of the node.
   */
  getNodeLabel() {
    return this.getCachedOpt().label
  }

  /**
   * Updates the label of the node.
   * @param {string} label - The new label for the node.
   */
  updateNodeLabel(label) {
    const attrs = {
      ...this.attributes.attrs,
      label: { ...this.attributes.attrs.label, text: label && label.length > 22 ? label.slice(0, 22) + '...' : label }
    }
    this.set('attrs', attrs, { rewrite: true })
  }

  /**
   * Updates the description of the node.
   * @param {string} description - The new description for the node.
   */
  updateNodeDescription(description) {
    const attrs = {
      ...this.attributes.attrs,
      description: { ...this.attributes.attrs.description, text: description || '' }
    }
    this.set('attrs', attrs, { rewrite: true })
  }

  /**
   * Caches the options for the node.
   * @param {CanvasWorkflowNode} opt - The options to cache.
   */
  setCacheOpt(opt) {
    util.setByPath(this, NodeOptCache, opt)
  }

  /**
   * Gets the cached options for the node.
   * @returns {CanvasWorkflowNode} The cached options.
   */
  getCachedOpt() {
    return util.getByPath(this, NodeOptCache)
  }
}
